export const BOOKING_INTENT_VERSION = "booking-intent-v1";

const text = (value = "") => String(value || "").trim();

const normalizeType = (value = "") => {
  const type = text(value).toLowerCase();
  if (type === "package") return "package";
  if (["room", "stay", "stays"].includes(type)) return "room";
  if (["cruise", "boating"].includes(type)) return "cruise";
  if (["trek", "trail", "trekking"].includes(type)) return "trek";
  return "safari";
};

const count = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : fallback;
};

const positiveCount = (value, fallback = 1) => Math.max(1, count(value, fallback) || fallback);

const dateKey = (value = "") => {
  if (!value) return "";
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  const raw = text(value);
  return raw.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] || raw;
};

const idOf = (value) => {
  if (value == null) return "";
  if (typeof value !== "object") return text(value);
  return text(value.id || value._id || value.key || value.code || value.slug || value.value);
};

const hasGuests = (source) =>
  Boolean(
    source &&
      typeof source === "object" &&
      !Array.isArray(source) &&
      ["adult", "adults", "adultCount", "child", "children", "childCount", "infant", "infants", "infantCount"]
        .some((key) => source[key] != null),
  );

const guestsOf = (source, fallback = null) => {
  const selected = hasGuests(source) ? source : hasGuests(fallback) ? fallback : {};
  const adults = count(selected.adult ?? selected.adults ?? selected.adultCount, 0);
  const children = count(selected.child ?? selected.children ?? selected.childCount, 0);
  const infants = count(selected.infant ?? selected.infants ?? selected.infantCount, 0);
  return { adults, children, infants, totalGuests: adults + children + infants };
};

const selectionList = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      id: idOf(item?.id || item?._id || item?.key || item?.slug || item?.label || item),
      quantity: positiveCount(item?.quantity ?? item?.qty ?? item?.count, 1),
    }))
    .filter((item) => item.id)
    .sort((left, right) => left.id.localeCompare(right.id) || left.quantity - right.quantity);

const groupIdsOf = (value = []) => {
  const source = Array.isArray(value) ? value : value ? [value] : [];
  return [...new Set(source.map(idOf).filter(Boolean))].sort();
};

export const buildBookingIntent = (input = {}) => {
  const embedded = input?.bookingIntent && typeof input.bookingIntent === "object" ? input.bookingIntent : {};
  const metadata = input?.metadata && typeof input.metadata === "object" ? input.metadata : {};
  const embeddedOccupancy = embedded.occupancy || {};
  const type = normalizeType(input.experienceType || input.type || embedded?.product?.type);
  const guests = guestsOf(input.guestMix || input.guests, embeddedOccupancy);
  const groupSelection = input.groupSelection || metadata.groupSelection || {};
  const startDate = type === "room"
    ? input.checkIn || metadata.checkIn || input.travelDate || input.date || embedded?.schedule?.startDate
    : input.travelDate || input.date || metadata.date || input.checkIn || embedded?.schedule?.startDate;
  const endDate = type === "room"
    ? input.checkOut || metadata.checkOut || metadata.checkout || embedded?.schedule?.endDate
    : "";

  return {
    version: BOOKING_INTENT_VERSION,
    product: {
      type,
      identifier: text(input.identifier || input.id || embedded?.product?.identifier),
    },
    schedule: {
      startDate: dateKey(startDate),
      endDate: dateKey(endDate),
    },
    occupancy: {
      units: positiveCount(input.quantity ?? input.qty ?? embeddedOccupancy.units, 1),
      ...guests,
    },
    selections: {
      entryPoint: text(input.entryPoint || embedded?.selections?.entryPoint),
      slotId: idOf(input.slot) || text(embedded?.selections?.slotId),
      vehicleId: idOf(input.vehicle) || text(embedded?.selections?.vehicleId),
      ratePlanId: text(
        input.ratePlanId || input.ratePlan?.id || metadata.ratePlanId || metadata.ratePlan || embedded?.selections?.ratePlanId,
      ),
      addOns: selectionList(input.addOns ?? embedded?.selections?.addOns),
      charges: selectionList(input.selectedCharges ?? embedded?.selections?.charges),
      groupIds: groupIdsOf(
        groupSelection.selectedGroupIds ||
          groupSelection.groupIds ||
          groupSelection.selectedGroupId ||
          embedded?.selections?.groupIds,
      ),
      groupMode: text(groupSelection.allocationMode || groupSelection.mode || embedded?.selections?.groupMode).toLowerCase(),
    },
    promotion: {
      couponCode: text(
        input.couponCode || metadata.couponCode || metadata.referralContext?.couponCode || embedded?.promotion?.couponCode,
      ).toUpperCase(),
    },
  };
};

export const withBookingIntent = (input = {}) => ({
  ...input,
  bookingIntent: buildBookingIntent(input),
});

export const applyBookingIntentToItem = (input = {}) => {
  const intent = input?.bookingIntent;
  if (!intent || intent.version !== BOOKING_INTENT_VERSION) return input;
  const canonical = buildBookingIntent({ bookingIntent: intent });
  const { occupancy, schedule, selections, product, promotion } = canonical;
  const guestMix = {
    adult: occupancy.adults,
    child: occupancy.children,
    infant: occupancy.infants,
    adults: occupancy.adults,
    children: occupancy.children,
    infants: occupancy.infants,
  };
  return {
    ...input,
    experienceType: product.type,
    identifier: product.identifier,
    quantity: occupancy.units,
    guestMix,
    travelDate: schedule.startDate,
    checkIn: schedule.startDate,
    checkOut: schedule.endDate,
    entryPoint: selections.entryPoint || input.entryPoint || "",
    couponCode: promotion.couponCode || input.couponCode || "",
    metadata: {
      ...(input.metadata || {}),
      date: product.type === "room" ? input?.metadata?.date : schedule.startDate,
      checkIn: schedule.startDate,
      checkOut: schedule.endDate,
      ratePlanId: selections.ratePlanId || input?.metadata?.ratePlanId || "",
      couponCode: promotion.couponCode || input?.metadata?.couponCode || "",
    },
  };
};

